import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { applyApiErrorsToForm } from '@/api/form-errors'
import { useMaintenance, useUpdateMaintenance, type MaintenanceState } from '@/maintenance/api'
import { maintenanceSchema, type MaintenanceFormValues } from '@/maintenance/schemas'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

interface ErrorBanner {
  message: string
  traceId?: string
}

/**
 * `/maintenance` — a publikus oldal elérhetőségének vezérlése (DEC-OPS-008).
 *
 * Kapcsoló + (opcionális) üzenet, amit karbantartás alatt a felhasználók
 * látnak. A backend `EnsureSiteAvailable` middleware-e a publikus végpontokra
 * 503-at ad az üzenettel; az admin-felület mindig elérhető marad.
 */
export function MaintenanceForm() {
  const { t } = useTranslation()
  const { data, isPending, isError } = useMaintenance()

  if (isPending) {
    return (
      <main className="mx-auto flex max-w-md flex-col gap-6 p-6">
        <p className="text-sm text-muted-foreground">{t('maintenance.loading')}</p>
      </main>
    )
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex max-w-md flex-col gap-6 p-6">
        <Alert variant="destructive">
          <AlertDescription>{t('maintenance.loadError')}</AlertDescription>
        </Alert>
      </main>
    )
  }

  return <MaintenanceFormInner initial={data} />
}

function MaintenanceFormInner({ initial }: { initial: MaintenanceState }) {
  const { t } = useTranslation()
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null)
  const [success, setSuccess] = useState(false)

  const form = useForm<MaintenanceFormValues>({
    resolver: zodResolver(maintenanceSchema),
    defaultValues: { enabled: initial.enabled, message: initial.message ?? '' },
  })

  const update = useUpdateMaintenance()

  function onSubmit(values: MaintenanceFormValues) {
    setErrorBanner(null)
    setSuccess(false)
    const trimmed = values.message.trim()
    update.mutate(
      { enabled: values.enabled, message: trimmed === '' ? null : trimmed },
      {
        onSuccess: (result) => {
          setSuccess(true)
          form.reset({ enabled: result.enabled, message: result.message ?? '' })
        },
        onError: (error) => {
          const applied = applyApiErrorsToForm(error, form.setError)
          if (applied.handled) return
          setErrorBanner({
            message: applied.message ?? t('common.error'),
            traceId: applied.traceId,
          })
        },
      },
    )
  }

  return (
    <main className="mx-auto flex max-w-md flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link to="/" className="self-start text-sm text-primary underline-offset-4 hover:underline">
          {t('maintenance.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('maintenance.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('maintenance.intro')}</p>
      </header>

      {success && (
        <Alert>
          <AlertDescription className="text-verified">{t('maintenance.success')}</AlertDescription>
        </Alert>
      )}

      {errorBanner && (
        <Alert variant="destructive">
          <AlertDescription>
            {errorBanner.message}
            {errorBanner.traceId && (
              <span className="block text-xs opacity-70">trace ID: {errorBanner.traceId}</span>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="enabled"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormControl>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormLabel className="!mt-0">{t('maintenance.enabledLabel')}</FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('maintenance.messageLabel')}</FormLabel>
                <FormControl>
                  <textarea
                    rows={3}
                    className="flex w-full rounded-md border border-input bg-input-background px-3 py-2 text-base shadow-[var(--input-shadow)] outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 md:text-sm"
                    placeholder={t('maintenance.messagePlaceholder')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" disabled={update.isPending} className="mt-2">
            {update.isPending ? t('maintenance.submitting') : t('maintenance.submit')}
          </Button>
        </form>
      </Form>
    </main>
  )
}
